import React, { useEffect, useLayoutEffect, useState } from 'react';

import { fabric } from 'fabric';

interface IUseImage {
  ref: React.MutableRefObject<fabric.Canvas>;
  src?: string;
  aspectRatio: number;
  onLoad?: (image: fabric.Image) => void;
}

export const useImage = (props: IUseImage) => {
  const { ref, src, aspectRatio, onLoad } = props;
  const [image, setImage] = useState<fabric.Image | undefined>();

  useLayoutEffect(() => {
    let isMounted = true;
    const canvas = ref.current;
    if (src) {
      fabric.Image.fromURL(src, (image) => {
        if (isMounted) {
          image.selectable = false;
          const width = canvas.getWidth();
          const height = canvas.getHeight();
          if (width > height) {
            image.scaleToHeight(width / aspectRatio);
          } else {
            image.scaleToWidth(height * aspectRatio);
          }
          canvas.setBackgroundImage(image, canvas.renderAll.bind(canvas));
          setImage(image);
        }
      }, {
        // positions backgroundImage at 0/0
        originX: 'left',
        originY: 'top',
        crossOrigin: 'Anonymous',
      });
    } else {
      canvas.clear();
    }
    return () => {
      isMounted = false;
    }
  }, [aspectRatio, ref, src]);

  useEffect(() => {
    if (image && onLoad) {
      onLoad(image);
    }
  }, [image, onLoad]);

  useEffect(() => {
    const canvas = ref.current;
    const resize = () => {
      if (image) {
        const width = canvas.getWidth();
        const height = canvas.getHeight();
        if (width && height) {
          if (width > height) {
            image.scaleToHeight(width / aspectRatio, true);
          } else {
            image.scaleToWidth(height * aspectRatio, true);
          }
          canvas.requestRenderAll();
        }
      }
    }
    const canvasEl = canvas.getElement();
    const observer = new ResizeObserver(resize);
    resize();
    observer.observe(canvasEl);
    return () => {
      observer.unobserve(canvasEl);
      observer.disconnect();
    };
  }, [aspectRatio, image, ref]);
}
